<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Check-In</h5>
        <p class="page-sub">Check in pre-registered visitors or walk-ins</p>
      </div>
      <router-link :to="$url('/hr/visitors/active')" class="btn-ghost">
        <font-awesome-icon icon="users" /> Active Visitors
      </router-link>
    </div>

    <div class="checkin-center">

    <!-- Mode tabs -->
    <div class="mode-tabs">
      <button :class="['mode-tab', mode==='qr' ? 'mode-tab--active' : '']" @click="mode='qr'" type="button">
        <font-awesome-icon icon="qrcode" /> Scan QR Code
      </button>
      <button :class="['mode-tab', mode==='walkin' ? 'mode-tab--active' : '']" @click="mode='walkin'" type="button">
        <font-awesome-icon icon="walking" /> Walk-In
      </button>
    </div>

    <!-- ── QR mode ── -->
    <div v-if="mode==='qr'" class="form-card" style="margin-top:16px;">
      <div class="section-label">Scan or Enter Visitor QR Token</div>

      <div v-if="!scannerActive" class="qr-placeholder" @click="startScanner">
        <font-awesome-icon icon="qrcode" class="qr-icon" />
        <p>Click to start camera scanner</p>
      </div>
      <div v-show="scannerActive" id="qr-reader" style="width:100%;max-width:400px;margin:0 auto 15px;"></div>
      <div v-if="scannerActive" style="display:flex;justify-content:center;margin-bottom:12px;">
        <button class="btn-ghost" @click="stopScanner">Stop Scanner</button>
      </div>

      <div class="form-row">
        <label class="form-label">Or paste QR token manually</label>
        <div style="display:flex;gap:8px;">
          <input v-model="qrToken" class="form-input" placeholder="Paste token here…" @keyup.enter="lookupQr" />
          <button class="btn-primary-action" @click="lookupQr" :disabled="!qrToken || qrLoading">
            {{ qrLoading ? '…' : 'Lookup' }}
          </button>
        </div>
      </div>

      <!-- Visit preview -->
      <div v-if="qrVisit" class="visit-preview">
        <div class="preview-top">
          <div class="vis-avatar vis-avatar--lg">{{ initials(qrVisit.visitor) }}</div>
          <div style="flex:1;">
            <div class="vis-name">{{ qrVisit.visitor?.first_name }} {{ qrVisit.visitor?.last_name }}</div>
            <div class="vis-sub">{{ qrVisit.visitor?.company }}</div>
          </div>
          <span class="chip" :class="statusChip(qrVisit.status)">{{ fmtStatus(qrVisit.status) }}</span>
        </div>
        <div class="detail-grid" style="margin-top:12px;">
          <div class="detail-row">
            <span class="detail-label">Host</span>
            <span class="detail-value">{{ hostName(qrVisit.host, qrVisit) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Purpose</span>
            <span class="detail-value">{{ qrVisit.purpose }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Scheduled</span>
            <span class="detail-value">{{ fmtDatetime(qrVisit.scheduled_at) }}</span>
          </div>
          <div v-if="qrVisitorType" class="detail-row">
            <span class="detail-label">Visitor Type</span>
            <span class="detail-value">
              <span class="vtype-chip" :style="{ background: qrVisitorType.colour + '22', color: qrVisitorType.colour }">
                <span class="vtype-chip-dot" :style="{ background: qrVisitorType.colour }"></span>
                {{ qrVisitorType.name }}
              </span>
            </span>
          </div>
        </div>

        <div v-if="qrVisit.status === 'pre_registered'" style="margin-top:16px;">
          <div class="form-cols" style="margin-bottom:14px;">
            <div class="form-row">
              <label class="form-label">Location / Room</label>
              <input v-model="checkInForm.location" class="form-input" placeholder="Optional" />
            </div>
            <div class="form-row">
              <label class="form-label">Items Deposited</label>
              <input v-model="checkInForm.items_deposited" class="form-input" placeholder="e.g. Laptop, Bag" />
            </div>
          </div>

          <!-- Locker picker (QR) -->
          <div class="form-row" style="margin-bottom:14px;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;">
              <label class="form-label" style="margin:0;">
                Assign Locker
                <span v-if="qrTypeRequiresLocker" class="locker-req-badge">Required</span>
              </label>
              <span v-if="availableLockers.length === 0 && qrTypeRequiresLocker" class="locker-none-warn">None available</span>
            </div>
            <select v-model="checkInForm.locker_id" class="form-input">
              <option :value="null">— No locker —</option>
              <option v-for="l in availableLockers" :key="l.id" :value="l.id">
                {{ l.number }}{{ l.label ? ` — ${l.label}` : '' }}
              </option>
            </select>
          </div>

          <!-- Face capture (QR) -->
          <div class="fc-box">
            <div class="section-label" style="margin-bottom:10px;"><font-awesome-icon icon="camera" style="margin-right:5px;" />Visitor Photo <span class="fc-optional">(optional)</span></div>
            <div v-if="checkInForm.photo" class="fc-preview-row">
              <img :src="checkInForm.photo" class="fc-thumb" alt="Visitor photo" />
              <button type="button" class="btn-ghost btn-sm" @click="retakePhoto('qr')"><font-awesome-icon icon="redo" /> Retake</button>
            </div>
            <div v-else-if="camera.active && camera.target === 'qr'" class="fc-camera-col">
              <video ref="videoQr" class="fc-video" autoplay playsinline muted></video>
              <div style="display:flex;gap:8px;margin-top:10px;">
                <button type="button" class="btn-primary-action" @click="capturePhoto('qr')"><font-awesome-icon icon="camera" /> Capture</button>
                <button type="button" class="btn-ghost" @click="stopCamera">Cancel</button>
              </div>
            </div>
            <div v-else class="fc-idle" @click="openCamera('qr')">
              <font-awesome-icon icon="user-circle" class="fc-idle-icon" />
              <span>Click to capture face photo</span>
              <p v-if="camera.error" class="fc-error">{{ camera.error }}</p>
            </div>
          </div>

          <button class="btn-primary-action" style="margin-top:16px;" @click="confirmCheckIn" :disabled="checkingIn">
            <font-awesome-icon v-if="checkingIn" icon="spinner" spin />
            {{ checkingIn ? 'Checking in…' : 'Confirm Check-In' }}
          </button>
        </div>
        <p v-else class="text-muted" style="margin-top:12px;font-size:13px;">This visit has already been processed.</p>
      </div>
    </div>

    <!-- ── Walk-in mode ── -->
    <div v-if="mode==='walkin'" class="form-card" style="margin-top:16px;">
      <div class="section-label">Walk-In Check-In</div>

      <div class="form-row" style="margin-bottom:14px;">
        <label class="form-label">Search existing visitor</label>
        <input v-model="searchQ" class="form-input" placeholder="Name or email…" @input="searchVisitors" />
        <div v-if="searchResults.length" class="search-dropdown">
          <div v-for="sr in searchResults" :key="sr.id" class="search-item" @click="prefillFrom(sr)">
            <span class="fw-600">{{ sr.first_name }} {{ sr.last_name }}</span>
            <span class="text-muted" style="margin-left:6px;">{{ sr.email }}</span>
            <span class="text-muted" style="margin-left:6px;">{{ sr.company }}</span>
          </div>
        </div>
      </div>

      <div class="form-cols">
        <div class="form-row">
          <label class="form-label">First Name <span class="req">*</span></label>
          <input v-model="walkInForm.first_name" class="form-input" required />
        </div>
        <div class="form-row">
          <label class="form-label">Last Name <span class="req">*</span></label>
          <input v-model="walkInForm.last_name" class="form-input" required />
        </div>
      </div>
      <div class="form-cols" style="margin-top:12px;">
        <div class="form-row">
          <label class="form-label">Email</label>
          <input v-model="walkInForm.email" type="email" class="form-input" />
        </div>
        <div class="form-row">
          <label class="form-label">Phone</label>
          <input v-model="walkInForm.phone" class="form-input" />
        </div>
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Company / Organisation</label>
        <input v-model="walkInForm.company" class="form-input" />
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Visitor Type <span class="req">*</span></label>
        <div class="vtype-select-wrap">
          <span class="vtype-dot" :style="{ background: selectedVisitorTypeColour || '#dde5f0' }"></span>
          <select v-model="walkInForm.visitor_type_id" class="form-input vtype-select">
            <option :value="null" disabled>— Select visitor type —</option>
            <option v-for="t in visitorTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>
      </div>
      <div class="form-cols" style="margin-top:12px;">
        <div class="form-row">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;">
            <label class="form-label" style="margin:0;">Host <span class="req">*</span></label>
            <div class="host-mode-toggle">
              <button type="button"
                :class="['host-mode-btn', hostMode === 'employee' ? 'host-mode-btn--active' : '']"
                @click="hostMode = 'employee'; walkInForm.host_name = ''">
                Employee
              </button>
              <button type="button"
                :class="['host-mode-btn', hostMode === 'text' ? 'host-mode-btn--active' : '']"
                @click="hostMode = 'text'; walkInForm.host_employee_id = ''">
                Free text
              </button>
            </div>
          </div>
          <search-select
            v-if="hostMode === 'employee'"
            v-model="walkInForm.host_employee_id"
            url="payroll/employees/active"
            query-param="search"
            label-key="full_name"
            sublabel-key="employee_no"
            value-key="id"
            response-key="data"
            placeholder="— Select host —"
            search-placeholder="Type name or number…"
            :min-chars="1"
          />
          <input
            v-else
            v-model="walkInForm.host_name"
            class="form-input"
            placeholder="e.g. Finance Department, Room 3B…"
          />
        </div>
        <div class="form-row">
          <label class="form-label">Purpose <span class="req">*</span></label>
          <input v-model="walkInForm.purpose" class="form-input" required />
        </div>
      </div>
      <div class="form-cols" style="margin-top:12px;">
        <div class="form-row">
          <label class="form-label">Location / Room</label>
          <input v-model="walkInForm.location" class="form-input" />
        </div>
        <div class="form-row">
          <label class="form-label">Items Deposited</label>
          <input v-model="walkInForm.items_deposited" class="form-input" placeholder="e.g. Laptop" />
        </div>
      </div>

      <!-- Locker -->
      <div class="form-row" style="margin-top:12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;">
          <label class="form-label" style="margin:0;">
            Assign Locker
            <span v-if="selectedTypeRequiresLocker" class="locker-req-badge">Required</span>
          </label>
          <span v-if="availableLockers.length === 0 && selectedTypeRequiresLocker" class="locker-none-warn">None available</span>
        </div>
        <select v-model="walkInForm.locker_id" class="form-input">
          <option :value="null">— No locker —</option>
          <option v-for="l in availableLockers" :key="l.id" :value="l.id">
            {{ l.number }}{{ l.label ? ` — ${l.label}` : '' }}
          </option>
        </select>
        <p v-if="availableLockers.length === 0 && !selectedTypeRequiresLocker" class="locker-hint">No lockers configured. Add them in Visitor Settings → Lockers.</p>
      </div>

      <!-- Face capture (walk-in) -->
      <div class="fc-box" style="margin-top:16px;">
        <div class="section-label" style="margin-bottom:10px;"><font-awesome-icon icon="camera" style="margin-right:5px;" />Visitor Photo <span class="fc-optional">(optional)</span></div>
        <div v-if="walkInForm.photo" class="fc-preview-row">
          <img :src="walkInForm.photo" class="fc-thumb" alt="Visitor photo" />
          <button type="button" class="btn-ghost btn-sm" @click="retakePhoto('walkin')"><font-awesome-icon icon="redo" /> Retake</button>
        </div>
        <div v-else-if="camera.active && camera.target === 'walkin'" class="fc-camera-col">
          <video ref="videoWalkin" class="fc-video" autoplay playsinline muted></video>
          <div style="display:flex;gap:8px;margin-top:10px;">
            <button type="button" class="btn-primary-action" @click="capturePhoto('walkin')"><font-awesome-icon icon="camera" /> Capture</button>
            <button type="button" class="btn-ghost" @click="stopCamera">Cancel</button>
          </div>
        </div>
        <div v-else class="fc-idle" @click="openCamera('walkin')">
          <font-awesome-icon icon="user-circle" class="fc-idle-icon" />
          <span>Click to capture face photo</span>
          <p v-if="camera.error" class="fc-error">{{ camera.error }}</p>
        </div>
      </div>

      <div style="display:flex;justify-content:flex-end;margin-top:20px;">
        <button class="btn-primary-action" @click="submitWalkIn" :disabled="walkingIn">
          <font-awesome-icon v-if="walkingIn" icon="spinner" spin />
          {{ walkingIn ? 'Checking in…' : 'Check In Visitor' }}
        </button>
      </div>
    </div>

    </div><!-- /checkin-center -->

    <!-- Hidden canvas -->
    <canvas ref="canvas" style="display:none;"></canvas>

    <!-- NDA / Agreement modal -->
    <div v-if="ndaModal" class="modal-overlay" @click.self="ndaModal = false">
      <div class="modal-box" style="max-width:540px;">
        <div class="modal-head">
          <h5 class="modal-title"><font-awesome-icon icon="file-contract" style="margin-right:6px;color:#004AAD;" />Visitor Agreement</h5>
          <button class="modal-close" @click="ndaModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="text-muted" style="margin:0 0 12px;">The visitor must read and accept the following agreement before checking in.</p>
          <div class="nda-text-box">{{ settings.nda_text }}</div>
          <label class="nda-accept-row">
            <input type="checkbox" v-model="ndaCheckbox" />
            <span>I confirm the visitor has read and agrees to the above terms</span>
          </label>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="ndaModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="!ndaCheckbox" @click="acceptNda">
            Proceed to Check-In
          </button>
        </div>
      </div>
    </div>

    <!-- Success / badge modal -->
    <div v-if="checkedInVisit" class="modal-overlay" @click.self="checkedInVisit = null">
      <div class="modal-box" style="max-width:560px;">
        <div class="modal-head">
          <h5 class="modal-title">
            <font-awesome-icon icon="check-circle" style="color:#16a34a;margin-right:6px;" />
            Visitor Checked In!
          </h5>
          <button class="modal-close" @click="checkedInVisit = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="padding:20px;">
          <!-- Printable badge -->
          <div id="ci-badge-printable">
            <div class="hbadge">
              <div class="hbadge-strip"></div>
              <div class="hbadge-content">
                <div class="hbadge-top">
                  <div class="hbadge-company">
                    <img v-if="company.logo" :src="company.logo" class="hbadge-logo" alt="" />
                    <span class="hbadge-company-name">{{ company.name }}</span>
                  </div>
                  <span class="hbadge-type-label">VISITOR</span>
                </div>
                <div class="hbadge-identity">
                  <div class="hbadge-photo-wrap">
                    <img v-if="checkedInVisit.photo_url || checkedInVisit.visitor?.photo_url" :src="checkedInVisit.photo_url || checkedInVisit.visitor.photo_url" class="hbadge-photo" alt="" />
                    <div v-else class="hbadge-initials">{{ badgeInitials(checkedInVisit) }}</div>
                  </div>
                  <div class="hbadge-name-block">
                    <div class="hbadge-name">{{ checkedInVisit.visitor?.first_name }} {{ checkedInVisit.visitor?.last_name }}</div>
                    <div v-if="checkedInVisit.visitor?.company" class="hbadge-org">{{ checkedInVisit.visitor.company }}</div>
                    <div v-if="checkedInVisit.visitor?.email" class="hbadge-email">{{ checkedInVisit.visitor.email }}</div>
                  </div>
                </div>
                <div class="hbadge-no-strip">
                  <svg class="hbadge-barcode-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="2" height="16"/><rect x="7" y="4" width="1" height="16"/><rect x="10" y="4" width="3" height="16"/><rect x="15" y="4" width="1" height="16"/><rect x="18" y="4" width="3" height="16"/></svg>
                  <span class="hbadge-no">{{ checkedInVisit.badge_no }}</span>
                </div>
                <div class="hbadge-details">
                  <div class="hbadge-detail">
                    <span class="hbd-label">Visiting</span>
                    <span class="hbd-value">{{ hostName(checkedInVisit.host, checkedInVisit) }}</span>
                  </div>
                  <div class="hbadge-detail">
                    <span class="hbd-label">Purpose</span>
                    <span class="hbd-value">{{ checkedInVisit.purpose }}</span>
                  </div>
                  <div class="hbadge-detail">
                    <span class="hbd-label">Check-in time</span>
                    <span class="hbd-value">{{ fmtDatetime(checkedInVisit.checked_in_at) }}</span>
                  </div>
                  <div v-if="checkedInVisit.locker" class="hbadge-detail">
                    <span class="hbd-label">Locker</span>
                    <span class="hbd-value hbd-locker">
                      <font-awesome-icon icon="box" style="margin-right:4px;color:#0f766e;" />
                      {{ checkedInVisit.locker.number }}
                      <span v-if="checkedInVisit.locker.label" class="locker-sub">{{ checkedInVisit.locker.label }}</span>
                    </span>
                  </div>
                </div>
                <div class="hbadge-instruction">{{ settings.badge_footer || 'Please wear this pass visibly at all times. Present to reception when leaving.' }}</div>
              </div>
            </div>
          </div>
        </div>
        <!-- Locker reassignment -->
        <div v-if="checkedInVisit" class="locker-reassign-bar">
          <span class="locker-reassign-label">
            <font-awesome-icon icon="box" />
            {{ checkedInVisit.locker ? `Locker ${checkedInVisit.locker.number}` : 'No locker assigned' }}
          </span>
          <div style="display:flex;align-items:center;gap:8px;">
            <select v-model="newLockerId" class="locker-select">
              <option value="">— Change locker —</option>
              <option v-for="l in availableLockers" :key="l.id" :value="l.id">{{ l.number }}{{ l.label ? ` (${l.label})` : '' }}</option>
              <option v-if="checkedInVisit.locker" value="__release">Release locker</option>
            </select>
            <button v-if="newLockerId" class="btn-ghost btn-sm" @click="doReassignLocker" :disabled="reassigningLocker">
              <font-awesome-icon v-if="reassigningLocker" icon="spinner" spin />
              <span v-else>Apply</span>
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="checkedInVisit = null">Close</button>
          <button class="btn-primary-action" @click="printBadge(checkedInVisit)">
            <font-awesome-icon icon="print" /> Print Pass
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import SearchSelect from '../../../../components/SearchSelect.vue'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'

let html5QrCode = null

export default {
  name: 'VisitorCheckIn',
  components: { SearchSelect },
  setup() { return { toast: useToast() } },

  data() {
    return {
      mode: 'qr',
      qrToken: '', qrVisit: null, qrLoading: false,
      scannerActive: false,
      checkInForm: { location: '', items_deposited: '', photo: null, locker_id: null },
      checkingIn: false,
      walkInForm: {
        first_name: '', last_name: '', email: '', phone: '', company: '',
        visitor_type_id: null,
        locker_id: null,
        host_employee_id: '', host_name: '', purpose: '', location: '', items_deposited: '', photo: null,
      },
      visitorTypes: [],
      hostMode: 'employee',
      walkingIn: false,
      searchQ: '', searchResults: [], searchTimer: null,
      camera: { active: false, target: null, stream: null, error: null },
      checkedInVisit: null,
      availableLockers: [],
      newLockerId: '',
      reassigningLocker: false,
      company: { name: '', logo: null },
      settings: { nda_enabled: false, nda_text: '', badge_footer: '' },
      ndaModal: false,
      ndaCheckbox: false,
      pendingSubmit: null,
    }
  },

  watch: {
    mode(v) {
      if (v === 'walkin') this.loadAvailableLockers()
    },
    checkedInVisit(v) {
      if (v) { this.newLockerId = ''; this.loadAvailableLockers() }
    },
  },

  computed: {
    selectedVisitorTypeColour() {
      const t = this.visitorTypes.find(t => t.id === this.walkInForm.visitor_type_id)
      return t ? t.colour : null
    },
    selectedTypeRequiresLocker() {
      const t = this.visitorTypes.find(t => t.id === this.walkInForm.visitor_type_id)
      return t?.requires_locker ?? false
    },
    qrVisitorType() {
      return this.qrVisit?.visitor_type ?? null
    },
    qrTypeRequiresLocker() {
      return this.qrVisitorType?.requires_locker ?? false
    },
  },

  created() {
    const u = this.$store.state.getLoggedInUser
    this.company = { name: u?.business_name ?? u?.company_name ?? '', logo: u?.business_logo ?? u?.logo ?? null }
    this.loadSettings()
    this.loadVisitorTypes()
    this.loadAvailableLockers()
  },

  beforeUnmount() {
    this.stopScanner()
    this.stopCamera()
  },

  methods: {
    async openCamera(target) {
      this.camera.error = null
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
        this.camera.stream = stream
        this.camera.target = target
        this.camera.active = true
        await this.$nextTick()
        const videoRef = target === 'qr' ? this.$refs.videoQr : this.$refs.videoWalkin
        if (videoRef) videoRef.srcObject = stream
      } catch {
        this.camera.error = 'Camera access denied. Please allow camera permissions.'
      }
    },
    capturePhoto(target) {
      const videoRef = target === 'qr' ? this.$refs.videoQr : this.$refs.videoWalkin
      const canvas   = this.$refs.canvas
      if (!videoRef || !canvas) return
      canvas.width  = videoRef.videoWidth
      canvas.height = videoRef.videoHeight
      canvas.getContext('2d').drawImage(videoRef, 0, 0)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      if (target === 'qr') { this.checkInForm.photo = dataUrl }
      else { this.walkInForm.photo = dataUrl }
      this.stopCamera()
    },
    retakePhoto(target) {
      if (target === 'qr') { this.checkInForm.photo = null }
      else { this.walkInForm.photo = null }
      this.openCamera(target)
    },
    stopCamera() {
      if (this.camera.stream) { this.camera.stream.getTracks().forEach(t => t.stop()) }
      this.camera = { active: false, target: null, stream: null, error: null }
    },
    async lookupQr() {
      if (!this.qrToken) return
      this.qrLoading = true
      try {
        const r = await axios.get('visitors/lookup', { params: { token: this.qrToken } })
        this.qrVisit = r.data.data
        if (this.qrVisit) this.loadAvailableLockers()
      } catch {
        this.toast.error('QR token not found.')
        this.qrVisit = null
      } finally { this.qrLoading = false }
    },
    async loadSettings() {
      try {
        const r = await axios.get('visitors/settings')
        const s = r.data.data
        this.settings = {
          nda_enabled:  s.nda_enabled  ?? false,
          nda_text:     s.nda_text     ?? '',
          badge_footer: s.badge_footer ?? '',
        }
      } catch { /* non-critical — silently skip */ }
    },
    async loadVisitorTypes() {
      try {
        const r = await axios.get('visitors/types')
        this.visitorTypes = (r.data.data ?? []).filter(t => t.active !== false)
      } catch { /* non-critical */ }
    },
    async loadAvailableLockers() {
      try {
        const r = await axios.get('visitors/lockers/available')
        this.availableLockers = r.data.data ?? []
      } catch { /* non-critical */ }
    },
    async doReassignLocker() {
      if (!this.newLockerId || !this.checkedInVisit) return
      this.reassigningLocker = true
      const lockerId = this.newLockerId === '__release' ? null : this.newLockerId
      try {
        const r = await axios.post('visitors/lockers/reassign', {
          visit_id:  this.checkedInVisit.id,
          locker_id: lockerId,
        })
        this.checkedInVisit = { ...this.checkedInVisit, locker: r.data.data?.locker ?? null }
        this.newLockerId = ''
        this.toast.success(lockerId ? 'Locker assigned.' : 'Locker released.')
        await this.loadAvailableLockers()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Could not update locker.')
      } finally { this.reassigningLocker = false }
    },
    async confirmCheckIn() {
      if (this.settings.nda_enabled) {
        this.pendingSubmit = 'qr'
        this.ndaCheckbox = false
        this.ndaModal = true
        return
      }
      await this.doCheckIn()
    },
    async doCheckIn() {
      this.checkingIn = true
      try {
        const r = await axios.post('visitors/check-in/qr', { qr_token: this.qrToken, ...this.checkInForm })
        this.checkedInVisit = r.data.data
        this.qrToken = ''; this.qrVisit = null
        this.checkInForm = { location: '', items_deposited: '', photo: null, locker_id: null }
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Check-in failed.')
      } finally { this.checkingIn = false }
    },
    async submitWalkIn() {
      if (this.settings.nda_enabled) {
        this.pendingSubmit = 'walkin'
        this.ndaCheckbox = false
        this.ndaModal = true
        return
      }
      await this.doWalkIn()
    },
    async doWalkIn() {
      this.walkingIn = true
      try {
        const r = await axios.post('visitors/walk-in', this.walkInForm)
        this.checkedInVisit = r.data.data
        Object.assign(this.walkInForm, {
          first_name: '', last_name: '', email: '', phone: '', company: '',
          visitor_type_id: null,
          locker_id: null,
          host_employee_id: '', host_name: '', purpose: '', location: '', items_deposited: '', photo: null,
        })
        this.loadAvailableLockers()
        this.hostMode = 'employee'
        this.searchQ = ''; this.searchResults = []
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Check-in failed.')
      } finally { this.walkingIn = false }
    },
    async acceptNda() {
      this.ndaModal = false
      const action = this.pendingSubmit
      this.pendingSubmit = null
      if (action === 'qr') await this.doCheckIn()
      else if (action === 'walkin') await this.doWalkIn()
    },
    searchVisitors() {
      clearTimeout(this.searchTimer)
      if (this.searchQ.length < 2) { this.searchResults = []; return }
      this.searchTimer = setTimeout(async () => {
        try {
          const r = await axios.get('visitors/search', { params: { q: this.searchQ } })
          this.searchResults = r.data.data ?? []
        } catch { /* silent */ }
      }, 300)
    },
    prefillFrom(v) {
      this.walkInForm.first_name = v.first_name
      this.walkInForm.last_name  = v.last_name
      this.walkInForm.email      = v.email ?? ''
      this.walkInForm.phone      = v.phone ?? ''
      this.walkInForm.company    = v.company ?? ''
      this.searchResults = []
      this.searchQ = `${v.first_name} ${v.last_name}`
    },
    async startScanner() {
      // Camera API requires a secure context (HTTPS or localhost)
      if (!window.isSecureContext) {
        this.toast.error('Camera access requires HTTPS. Please use the manual token entry below.')
        return
      }
      // Check for MediaDevices API support (all modern browsers)
      if (!navigator.mediaDevices?.getUserMedia) {
        this.toast.error('Your browser does not support camera access. Please paste the QR token manually.')
        return
      }
      this.scannerActive = true
      await this.$nextTick()
      try {
        html5QrCode = new Html5Qrcode('qr-reader', {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        })
        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => { this.qrToken = decodedText; this.stopScanner(); this.lookupQr() },
          () => {}
        )
      } catch (err) {
        this.scannerActive = false
        html5QrCode = null
        const msg = err?.message ?? ''
        if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('denied')) {
          this.toast.error('Camera permission denied. Please allow camera access and try again.')
        } else if (msg.toLowerCase().includes('notfound') || msg.toLowerCase().includes('no camera')) {
          this.toast.error('No camera found on this device.')
        } else {
          this.toast.error('Could not start camera scanner. Please paste the QR token manually.')
        }
      }
    },
    async stopScanner() {
      if (html5QrCode) {
        try { await html5QrCode.stop() } catch { /* ignore */ }
        html5QrCode = null
      }
      this.scannerActive = false
    },
    printBadge() {
      const el = document.getElementById('ci-badge-printable')
      if (!el) return
      const win = window.open('', '_blank', 'width=620,height=900')
      win.document.write(`<!DOCTYPE html><html><head><title>Visitor Pass</title><style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#f4f7fb;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .hbadge{width:480px;background:#fff;border-radius:20px;box-shadow:0 24px 64px rgba(0,0,0,.18);display:flex;overflow:hidden;}
        .hbadge-strip{width:8px;background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%);flex-shrink:0;}
        .hbadge-content{flex:1;padding:24px 24px 20px;}
        .hbadge-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
        .hbadge-company{display:flex;align-items:center;gap:8px;}
        .hbadge-logo{height:24px;width:auto;object-fit:contain;background:#fff;border-radius:4px;padding:2px;}
        .hbadge-company-name{font-size:13px;font-weight:700;color:#1a2b45;}
        .hbadge-type-label{font-size:9px;font-weight:800;letter-spacing:.14em;color:#fff;background:#004AAD;padding:3px 9px;border-radius:4px;}
        .hbadge-identity{display:flex;align-items:center;gap:16px;margin-bottom:20px;}
        .hbadge-photo-wrap{flex-shrink:0;}
        .hbadge-photo{width:72px;height:72px;border-radius:12px;object-fit:cover;border:2px solid #e0e7ff;}
        .hbadge-initials{width:72px;height:72px;border-radius:12px;background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%);color:#fff;font-size:24px;font-weight:800;display:flex;align-items:center;justify-content:center;}
        .hbadge-name-block{min-width:0;}
        .hbadge-name{font-size:20px;font-weight:800;color:#1a2b45;line-height:1.2;margin-bottom:2px;}
        .hbadge-org{font-size:12px;color:#7a90b0;margin-bottom:1px;}
        .hbadge-email{font-size:11px;color:#aab8cc;}
        .hbadge-no-strip{display:flex;align-items:center;gap:10px;background:#f4f7fb;border-radius:8px;padding:9px 14px;margin-bottom:16px;}
        .hbadge-barcode-icon{color:#c0cad6;flex-shrink:0;}
        .hbadge-no{font-size:16px;font-weight:800;color:#1a2b45;letter-spacing:.08em;}
        .hbadge-details{display:flex;flex-direction:column;border-top:1px solid #f0f4f8;margin-bottom:16px;}
        .hbadge-detail{display:flex;justify-content:space-between;align-items:baseline;padding:9px 0;border-bottom:1px solid #f0f4f8;}
        .hbadge-detail:last-child{border-bottom:none;}
        .hbd-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#aab8cc;}
        .hbd-value{font-size:13px;font-weight:600;color:#1a2b45;text-align:right;max-width:65%;}
        .hbadge-instruction{font-size:11px;color:#c0cad6;text-align:center;line-height:1.5;padding-top:12px;border-top:1px solid #f0f4f8;}
        @media print{body{background:#fff;min-height:auto;}.hbadge{box-shadow:none;border:1px solid #e4eaf2;}}
      </style></head><body>${el.outerHTML}</body></html>`)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 300)
    },
    badgeInitials(v) { return ((v?.visitor?.first_name?.[0] ?? '') + (v?.visitor?.last_name?.[0] ?? '')).toUpperCase() },
    initials(v) { return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase() },
    hostName(h, visit) {
      if (h) return `${h.first_name} ${h.last_name}`
      return visit?.host_name || '—'
    },
    fmtDatetime(v) {
      if (!v) return '—'
      return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    },
    fmtStatus(s) {
      const map = { pre_registered: 'Pre-Registered', checked_in: 'Checked In',
        checked_out: 'Checked Out', no_show: 'No Show', cancelled: 'Cancelled' }
      return map[s] ?? s
    },
    statusChip(s) {
      return { pre_registered: 'chip--indigo', checked_in: 'chip--green', checked_out: 'chip--purple',
               no_show: 'chip--amber', cancelled: 'chip--red' }[s] ?? 'chip--gray'
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-sm { height:32px; font-size:12px; padding:0 12px; }

.checkin-center { max-width:680px; margin:0 auto; }
.mode-tabs { display:inline-flex; border:1px solid #e4eaf2; border-radius:8px; overflow:hidden; }
.mode-tab { padding:9px 20px; background:#f8fafc; border:none; cursor:pointer; font-size:13px; font-weight:500; color:#7a90b0; font-family:inherit; display:inline-flex; align-items:center; gap:6px; }
.mode-tab--active { background:#004AAD; color:#fff; }
.mode-tab:not(.mode-tab--active):hover { background:#f0f4f8; }

.form-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px; }
.form-cols  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-row   { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }

.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }

.chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--gray   { background:#f1f5f9; color:#64748b; }
.chip--indigo { background:#e0e7ff; color:#4338ca; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fce7f3; color:#be185d; }

.visit-preview { border:1px solid #e4eaf2; border-radius:10px; padding:16px; margin-top:16px; }
.preview-top   { display:flex; align-items:center; gap:10px; }

.qr-placeholder { border:2px dashed #dde5f0; border-radius:10px; padding:40px; text-align:center; cursor:pointer; color:#aab8cc; }
.qr-placeholder:hover { background:#f8fafc; }
.qr-icon { font-size:2.5rem; margin-bottom:8px; display:block; }

.search-dropdown { border:1px solid #e4eaf2; border-radius:8px; background:#fff; box-shadow:0 4px 16px rgba(0,0,0,.1); margin-top:4px; }
.search-item { padding:8px 12px; cursor:pointer; font-size:13px; color:#1a2b45; }
.search-item:hover { background:#f8fafc; }

/* Face capture */
.fc-box { border:1px solid #e4eaf2; border-radius:10px; padding:16px; background:#f8fafc; }
.fc-optional { font-weight:400; color:#aab8cc; text-transform:none; letter-spacing:0; font-size:11px; margin-left:4px; }
.fc-idle { display:flex; flex-direction:column; align-items:center; gap:6px; padding:24px; border:2px dashed #dde5f0; border-radius:8px; cursor:pointer; color:#aab8cc; font-size:13px; }
.fc-idle:hover { background:#f0f4f8; }
.fc-idle-icon { font-size:2.4rem; color:#dde5f0; }
.fc-error { color:#dc2626; font-size:12px; margin:4px 0 0; }
.fc-camera-col { display:flex; flex-direction:column; align-items:center; }
.fc-video { width:100%; max-width:320px; border-radius:8px; background:#000; }
.fc-preview-row { display:flex; align-items:center; gap:14px; }
.fc-thumb { width:80px; height:80px; border-radius:50%; object-fit:cover; border:3px solid #e4eaf2; }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box   { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:92vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; line-height:1; }
.modal-body  { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.modal-photo { width:80px; height:80px; border-radius:50%; object-fit:cover; border:3px solid #e4eaf2; display:block; margin:0 auto; }

.detail-grid { background:#f8fafc; border-radius:8px; padding:12px 14px; }
.detail-row  { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f0f4f8; font-size:13px; }
.detail-row:last-child { border-bottom:none; }
.detail-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#aab8cc; }
.detail-value { font-weight:600; color:#1a2b45; font-size:13px; }

.vis-avatar { width:32px; height:32px; border-radius:50%; background:#e0e7ff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#4338ca; flex-shrink:0; }
.vis-avatar--lg { width:40px; height:40px; font-size:14px; }
.vis-name   { font-weight:600; font-size:13px; color:#1a2b45; }
.vis-sub    { font-size:11px; color:#7a90b0; }
.fw-600 { font-weight:600; }
.text-muted { color:#7a90b0; font-size:12px; }

/* ── Printable badge (check-in success modal) ─────────────────────────── */
.hbadge { display:flex; overflow:hidden; border-radius:16px; border:1px solid #e4eaf2; background:#fff; }
.hbadge-strip { width:8px; background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%); flex-shrink:0; }
.hbadge-content { flex:1; padding:20px 20px 16px; }
.hbadge-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.hbadge-company { display:flex; align-items:center; gap:8px; }
.hbadge-logo { height:22px; width:auto; object-fit:contain; border-radius:4px; padding:2px; background:#fff; }
.hbadge-company-name { font-size:12px; font-weight:700; color:#1a2b45; }
.hbadge-type-label { font-size:9px; font-weight:800; letter-spacing:.14em; color:#fff; background:#004AAD; padding:3px 8px; border-radius:4px; }
.hbadge-identity { display:flex; align-items:center; gap:14px; margin-bottom:16px; }
.hbadge-photo-wrap { flex-shrink:0; }
.hbadge-photo { width:64px; height:64px; border-radius:10px; object-fit:cover; border:2px solid #e0e7ff; }
.hbadge-initials { width:64px; height:64px; border-radius:10px; background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%); color:#fff; font-size:22px; font-weight:800; display:flex; align-items:center; justify-content:center; }
.hbadge-name-block { min-width:0; }
.hbadge-name { font-size:18px; font-weight:800; color:#1a2b45; line-height:1.2; margin-bottom:2px; }
.hbadge-org { font-size:12px; color:#7a90b0; }
.hbadge-email { font-size:11px; color:#aab8cc; }
.hbadge-no-strip { display:flex; align-items:center; gap:8px; background:#f4f7fb; border-radius:7px; padding:8px 12px; margin-bottom:14px; }
.hbadge-barcode-icon { color:#c0cad6; flex-shrink:0; }
.hbadge-no { font-size:15px; font-weight:800; color:#1a2b45; letter-spacing:.08em; }
.hbadge-details { display:flex; flex-direction:column; border-top:1px solid #f0f4f8; margin-bottom:12px; }
.hbadge-detail { display:flex; justify-content:space-between; align-items:baseline; padding:8px 0; border-bottom:1px solid #f0f4f8; }
.hbadge-detail:last-child { border-bottom:none; }
.hbd-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.hbd-value { font-size:13px; font-weight:600; color:#1a2b45; text-align:right; max-width:65%; }
.hbadge-instruction { font-size:11px; color:#c0cad6; text-align:center; line-height:1.5; padding-top:10px; border-top:1px solid #f0f4f8; }

/* Locker picker in walk-in form */
.locker-req-badge { display:inline-block; background:#ccfbf1; color:#0f766e; font-size:10px; font-weight:700; padding:1px 7px; border-radius:10px; margin-left:6px; vertical-align:middle; }
.locker-none-warn { font-size:11px; color:#d97706; font-weight:600; }
.locker-hint      { font-size:11px; color:#aab8cc; margin:3px 0 0; }

/* Locker in badge */
.hbd-locker { display:inline-flex; align-items:center; }
.locker-sub { margin-left:5px; font-size:11px; color:#aab8cc; font-weight:400; }

/* Locker reassign bar */
.locker-reassign-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; padding:10px 22px; border-top:1px solid #f0f4f8; background:#f8fafc; }
.locker-reassign-label { font-size:12px; font-weight:600; color:#3d5068; display:inline-flex; align-items:center; gap:6px; }
.locker-select { padding:6px 10px; border:1px solid #dde5f0; border-radius:7px; font-size:12px; color:#1a2b45; background:#fff; outline:none; font-family:inherit; }
.locker-select:focus { border-color:#004AAD; }

/* Visitor type select */
.vtype-select-wrap { position:relative; display:flex; align-items:center; }
.vtype-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; position:absolute; left:12px; z-index:1; pointer-events:none; }
.vtype-select { padding-left:30px !important; }

/* Visitor type chip (QR preview) */
.vtype-chip { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:20px; font-size:12px; font-weight:600; }
.vtype-chip-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }

/* Host mode toggle */
.host-mode-toggle { display:inline-flex; border:1px solid #dde5f0; border-radius:6px; overflow:hidden; }
.host-mode-btn { padding:3px 10px; background:#f8fafc; border:none; cursor:pointer; font-size:11px; font-weight:600; color:#7a90b0; font-family:inherit; }
.host-mode-btn--active { background:#004AAD; color:#fff; }
.host-mode-btn:not(.host-mode-btn--active):hover { background:#f0f4f8; }

/* NDA modal */
.nda-text-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:14px 16px; font-size:13px; color:#3d5068; line-height:1.7; max-height:240px; overflow-y:auto; white-space:pre-wrap; }
.nda-accept-row { display:flex; align-items:flex-start; gap:10px; font-size:13px; color:#1a2b45; font-weight:500; cursor:pointer; margin-top:14px; }
.nda-accept-row input[type=checkbox] { margin-top:2px; width:16px; height:16px; flex-shrink:0; cursor:pointer; accent-color:#004AAD; }

@media (max-width:600px) { .form-cols { grid-template-columns:1fr; } }
</style>
